import { supabase } from '../../../shared/lib/supabase';
import { VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { TacticsImage } from '../../../shared/services/tacticsImagesService';

export interface TournamentWithMatches {
  id: string;
  name: string;
  matches: MatchWithAssets[];
}

export interface MatchWithAssets {
  id: string;
  name: string;
  tournament_id: string;
  tournament_name: string;
  highlights: VideoHighlight[];
  tactics: TacticsImage[];
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service for fetching highlights and tactics with access control
 */
export const highlightsTacticsService = {
  /**
   * Get accessible tournaments with matches that have highlights or tactics
   * Only shows matches that have at least one completed highlight or uploaded tactics image
   */
  async getTournamentsWithMatches(orgId: string): Promise<ServiceResult<TournamentWithMatches[]>> {
    try {
      // Step 1: Get accessible tournament IDs
      const { data: tournamentAccess, error: taError } = await supabase
        .from('tournament_access')
        .select('tournament_id')
        .eq('organisation_id', orgId)
        .eq('is_active', true);

      if (taError) {
        console.error('Error fetching tournament access:', taError);
        return { success: false, error: taError.message };
      }

      const accessibleTournamentIds = (tournamentAccess || []).map(ta => ta.tournament_id);
      if (accessibleTournamentIds.length === 0) {
        return { success: true, data: [] };
      }

      // Step 2: Get accessible team IDs
      const { data: teamAccess, error: teamError } = await supabase
        .from('team_access')
        .select('team_id')
        .eq('organisation_id', orgId)
        .eq('active', true);

      if (teamError) {
        console.error('Error fetching team access:', teamError);
        return { success: false, error: teamError.message };
      }

      const accessibleTeamIds = (teamAccess || []).map(ta => ta.team_id);
      if (accessibleTeamIds.length === 0) {
        return { success: true, data: [] };
      }

      // Step 3: Get matches from accessible tournaments
      const { data: matches, error: matchesError } = await supabase
        .from('matches')
        .select(`
          id,
          name,
          tournament_id,
          home_team_id,
          away_team_id,
          tournaments!inner(id, name)
        `)
        .in('tournament_id', accessibleTournamentIds)
        .eq('active', true);

      if (matchesError) {
        console.error('Error fetching matches:', matchesError);
        return { success: false, error: matchesError.message };
      }

      // Step 4: Filter matches by team access (home or away team must be accessible)
      const accessibleMatches = (matches || []).filter(
        (match: any) =>
          accessibleTeamIds.includes(match.home_team_id) ||
          accessibleTeamIds.includes(match.away_team_id)
      );

      if (accessibleMatches.length === 0) {
        return { success: true, data: [] };
      }

      const matchIds = accessibleMatches.map((m: any) => m.id);

      // Step 5: Get completed and stitched highlights for these matches
      const { data: highlights, error: highlightsError } = await supabase
        .from('video_highlights')
        .select('*')
        .in('match_id', matchIds)
        .in('status', ['stitched', 'completed'])
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (highlightsError) {
        console.error('Error fetching highlights:', highlightsError);
        return { success: false, error: highlightsError.message };
      }

      // Step 6: Get uploaded tactics images for these matches
      const { data: tactics, error: tacticsError } = await supabase
        .from('tactics_images')
        .select('*')
        .in('match_id', matchIds)
        .eq('status', 'uploaded')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (tacticsError) {
        console.error('Error fetching tactics images:', tacticsError);
        return { success: false, error: tacticsError.message };
      }

      // Step 7: Group highlights and tactics by match
      const highlightsByMatch = new Map<string, VideoHighlight[]>();
      (highlights || []).forEach((highlight: VideoHighlight) => {
        const existing = highlightsByMatch.get(highlight.match_id) || [];
        existing.push(highlight);
        highlightsByMatch.set(highlight.match_id, existing);
      });

      const tacticsByMatch = new Map<string, TacticsImage[]>();
      (tactics || []).forEach((tactic: TacticsImage) => {
        const existing = tacticsByMatch.get(tactic.match_id) || [];
        existing.push(tactic);
        tacticsByMatch.set(tactic.match_id, existing);
      });

      // Step 8: Build match objects with assets, only include matches that have at least one highlight or tactic
      const matchesWithAssets: MatchWithAssets[] = accessibleMatches
        .map((match: any) => {
          const matchHighlights = highlightsByMatch.get(match.id) || [];
          const matchTactics = tacticsByMatch.get(match.id) || [];

          // Only include matches that have at least one highlight or tactic
          if (matchHighlights.length === 0 && matchTactics.length === 0) {
            return null;
          }

          return {
            id: match.id,
            name: match.name,
            tournament_id: match.tournament_id,
            tournament_name: match.tournaments?.name || '',
            highlights: matchHighlights,
            tactics: matchTactics,
          };
        })
        .filter((m): m is MatchWithAssets => m !== null);

      // Step 9: Group matches by tournament
      const tournamentsMap = new Map<string, TournamentWithMatches>();
      matchesWithAssets.forEach((match) => {
        const existing = tournamentsMap.get(match.tournament_id);
        if (existing) {
          existing.matches.push(match);
        } else {
          tournamentsMap.set(match.tournament_id, {
            id: match.tournament_id,
            name: match.tournament_name,
            matches: [match],
          });
        }
      });

      const result = Array.from(tournamentsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      return { success: true, data: result };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getTournamentsWithMatches:', message);
      return { success: false, error: message };
    }
  },
};

